import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { parse } from 'graphql'
import { ReactNode, useMemo, useState } from 'react'
import { useQuery, UseQueryArgs } from 'urql'
import { classNames } from '../../_helpers'
import { Button } from '../../_ui/Button'
import { Dialog } from '../../_ui/Dialog'
import { Spinner } from '../../_ui'

type SeasonCheckinItemsDialogItem = {
  id: string
  title?: string | null
  type?: string | null
  quantity?: number | null
  issuedAmount?: number | null
  remainingAmount?: number | null
  propertyOptionQuantities: Array<{
    id: string
    quantity?: number | null
    issuedAmount?: number | null
    requestedAmount?: number | null
    propertyOption?: {
      id: string
      title?: string | null
    } | null
  }>
}

type SeasonCheckinItemsDialogQueryData = {
  readCurrentSeason?: {
    id: string
    seasonCheckinItems: SeasonCheckinItemsDialogItem[]
  } | null
}

const seasonCheckinItemsDialogQuery = parse(/* GraphQL */ `
  query ReadSeasonCheckinItemsDialog {
    readCurrentSeason {
      id
      seasonCheckinItems {
        id
        title
        type
        quantity
        issuedAmount
        remainingAmount
        propertyOptionQuantities {
          id
          quantity
          issuedAmount
          requestedAmount
          propertyOption {
            id
            title
          }
        }
      }
    }
  }
`)

type PropTypes = {
  buttonLabel?: ReactNode
  title?: ReactNode
  className?: string
}

export const SeasonCheckinItemsDialog = ({ buttonLabel = 'Bestand', title = 'Check-In-Bestand', className }: PropTypes) => {
  const [open, setOpen] = useState(false)

  const [inventoryResult] = useQuery({
    query: seasonCheckinItemsDialogQuery,
    pause: !open,
  } as UseQueryArgs)

  const inventoryData = inventoryResult.data as SeasonCheckinItemsDialogQueryData | undefined

  const items = useMemo<SeasonCheckinItemsDialogItem[]>(() => {
    return inventoryData?.readCurrentSeason?.seasonCheckinItems || []
  }, [inventoryData?.readCurrentSeason?.seasonCheckinItems])

  const quantityItems = useMemo<SeasonCheckinItemsDialogItem[]>(() => {
    return items
      .filter(item => item.type === 'quantity' || item.type === 'property')
      .sort((left, right) => {
        const leftTitle = left.title || ''
        const rightTitle = right.title || ''
        return leftTitle.localeCompare(rightTitle, 'de')
      })
  }, [items])

  return (
    <>
      <Button
        size={'small'}
        color={'default'}
        onClick={() => setOpen(true)}
        icon={<ChevronDownIcon className={'w-4 h-4'} />}
      >
        {buttonLabel}
      </Button>

      {open && (
        <Dialog
          title={title}
          open={open}
          onClose={() => setOpen(false)}
          className={classNames('sm:max-w-2xl', className)}
        >
          {inventoryResult.fetching && !inventoryResult.data ? (
            <Spinner size={'w-8 h-8'} color={'text-gray-400'} />
          ) : quantityItems.length > 0 ? (
            <div className={'space-y-3 max-h-[70vh] overflow-y-auto pr-1'}>
              {quantityItems.map(item => {
                const issuedAmount = item.issuedAmount || 0
                const configuredAmount = item.type === 'property'
                  ? item.propertyOptionQuantities.reduce((sum, optionQuantity) => sum + (optionQuantity.quantity || 0), 0)
                  : (item.quantity || 0)
                const remainingAmount = item.remainingAmount ?? Math.max(configuredAmount - issuedAmount, 0)

                return (
                  <div key={item.id} className={'rounded-lg border border-gray-200 bg-gray-50 p-3'}>
                    <div className={'flex items-start justify-between gap-3'}>
                      <div>
                        <div className={'font-medium text-gray-900'}>{item.title}</div>
                        <div className={'text-xs text-gray-500 mt-1'}>
                          {item.type === 'property' ? 'Property-basiert' : 'Gesamtmenge'}
                        </div>
                      </div>

                      <div className={'text-right text-sm'}>
                        <div className={'font-medium text-gray-900'}>
                          {issuedAmount} / {configuredAmount}
                        </div>
                        <div className={'text-xs text-gray-500'}>
                          {remainingAmount} verfügbar
                        </div>
                      </div>
                    </div>

                    <div className={'mt-3 h-2 rounded-full bg-gray-200 overflow-hidden'}>
                      <div
                        className={'h-full rounded-full bg-primary-300'}
                        style={{
                          width: `${configuredAmount > 0 ? Math.min((issuedAmount / configuredAmount) * 100, 100) : 0}%`
                        }}
                      />
                    </div>

                    {item.type === 'property' && item.propertyOptionQuantities.length > 0 && (
                      <div className={'mt-3 border-t border-gray-200 pt-3'}>
                        <div className={'text-xs font-medium uppercase tracking-wide text-gray-500 mb-2'}>
                          Verteilung nach Property-Option
                        </div>
                        <div className={'grid gap-2'}>
                          {item.propertyOptionQuantities.map(optionQuantity => {
                            const optionTitle = optionQuantity.propertyOption?.title || 'Unbekannte Option'
                            const optionAmount = optionQuantity.quantity || 0
                            const issuedAmount = optionQuantity.issuedAmount || 0
                            const requestedAmount = optionQuantity.requestedAmount ?? Math.max(optionAmount - issuedAmount, 0)

                            return (
                              <div key={optionQuantity.id} className={'flex items-center justify-between text-sm'}>
                                <div className={'text-gray-700'}>{optionTitle}</div>
                                <div className={'text-right font-medium text-gray-900'}>
                                  <div>{issuedAmount} / {optionAmount}</div>
                                  <div className={'text-xs text-gray-500'}>{requestedAmount} noch auszugeben</div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'text-sm text-gray-600'}>
              Keine Check-In-Items gefunden.
            </div>
          )}
        </Dialog>
      )}
    </>
  )
}
